import b from 'b_';
import { mediaQueryResolver, SCREEN_SIZE, type WithMediaQueryResolverProps } from 'platform-components/hocs';
import React, { useEffect, useState } from 'react';
import { type WrappedComponentProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { type Action, type Dispatch, bindActionCreators } from 'redux';

import { selectors as lessonSelectors } from 'common.components/lesson';
import { SCREEN_QUERY } from 'common.components/lesson-view.common/constants';
import * as selectors from 'common.components/lesson-view.side-panel/selectors';
import * as lessonStatsSelectors from 'common.components/lesson-view.stats/selectors';
import { getTeamIdFromPath } from 'utils/path';

//@ts-ignore
import BREAKPOINTS from '../../design/breakpoints.scss';

import { SidePanelModeAfterCopying } from './_mode/side-panel_mode_after-copying';
import { SidePanelModeAssignment } from './_mode/side-panel_mode_assignment';
import { SidePanelModeCopy } from './_mode/side-panel_mode_copy';
import { SidePanelModeCopyAsRno } from './_mode/side-panel_mode_copy-as-rno';
import { SidePanelModeLessonStats } from './_mode/side-panel_mode_lesson-stats';
import { SidePanelModeLoading } from './_mode/side-panel_mode_loading';
import { actions } from './actions';
import { SIDE_PANEL_MODE } from './side-panel-mode';
import { LessonViewSidePanelStore } from './typings';

import './side-panel.scss';
import './side-panel.mobile.scss';

interface OwnProps {
    onForceComplete?: () => void;
    isCopyingEnabled?: boolean;
    hideFooter?: boolean;
}

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type HOCProps = WithMediaQueryResolverProps & WrappedComponentProps;

interface Props extends OwnProps, StoreProps, DispatchProps, HOCProps {}

// eslint-disable-next-line complexity
export const SidePanel = ({
    isEmptyStats,
    clesson,
    mode,
    screenSize,
    isExpanded,
    onForceComplete,
    setExpanded,
    shouldCollapseFooter,
    isCopyingEnabled,
    hideFooter,
}: Props) => {
    let content;
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const isSmallScreen = screenSize && screenSize === SCREEN_SIZE.SMALL;
        const shouldExpand = !isSmallScreen || mode === SIDE_PANEL_MODE.REASSIGNMENT;

        setIsMobile(isSmallScreen);
        setExpanded(shouldExpand);

        return () => {
            setExpanded(true);
        };
    }, [screenSize, mode, setExpanded]);

    switch (mode) {
        case SIDE_PANEL_MODE.LOADING:
            content = <SidePanelModeLoading />;
            break;
        case SIDE_PANEL_MODE.COPY:
            content = <SidePanelModeCopy />;
            break;
        case SIDE_PANEL_MODE.COPY_AS_RNO:
            content = <SidePanelModeCopyAsRno />;
            break;
        case SIDE_PANEL_MODE.LESSON_STATS:
            content = (
                <SidePanelModeLessonStats
                    hideFooter={hideFooter}
                    isCopyingEnabled={isCopyingEnabled}
                    isMobile={isMobile}
                    onForceComplete={onForceComplete}
                />
            );
            break;
        case SIDE_PANEL_MODE.ASSIGNMENT:
        case SIDE_PANEL_MODE.REASSIGNMENT:
            content = (
                <SidePanelModeAssignment
                    isCopyingEnabled={isCopyingEnabled}
                    isExpanded={isExpanded}
                    isMobile={isMobile}
                    onForceComplete={onForceComplete}
                />
            );
            break;
        case SIDE_PANEL_MODE.AFTER_COPYING:
            content = (
                <SidePanelModeAfterCopying
                    clessonMode={clesson?.clesson?.mode || null}
                    isCopyingEnabled={isCopyingEnabled}
                    isExpanded={isExpanded}
                    isMobile={isMobile}
                    onForceComplete={onForceComplete}
                />
            );
            break;
        default:
            content = null;
    }

    const wrapperClassName = b('side-panel', 'wrapper', {
        expanded: isExpanded,
        'collapsed-footer': shouldCollapseFooter,
        mode: mode.replace(/_/, '-'),
        'empty-stats': isEmptyStats,
        mobile: isMobile,
    });
    const sidePanelClassName = b('side-panel', {
        loading: mode === SIDE_PANEL_MODE.LOADING,
        'collapsed-footer': shouldCollapseFooter,
        expanded: isExpanded,
        mode: mode.replace(/_/, '-'),
    });

    return (
        <div className={wrapperClassName}>
            <div className={sidePanelClassName}>{content}</div>
        </div>
    );
};

const mapStateToProps = <T extends { lessonViewSidePanel: LessonViewSidePanelStore }>(storeState: T) => {
    const classId = getTeamIdFromPath();
    const clesson = lessonSelectors.getLesson(storeState)!;

    return {
        clesson,
        mode: selectors.getSidePanelMode(storeState),
        isExpanded: selectors.getIsExpanded(storeState),
        shouldCollapseFooter: selectors.getShouldCollapseFooter(storeState),
        isEmptyStats: lessonStatsSelectors.getAreProblemStatsEmpty(storeState, { classId }),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) =>
    bindActionCreators({ setExpanded: actions.setExpanded }, dispatch);

export default mediaQueryResolver(
    [],
    BREAKPOINTS,
    SCREEN_QUERY
)(injectIntl(connect(mapStateToProps, mapDispatchToProps)(SidePanel))) as React.ComponentType<OwnProps>;
